import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { RotateCcw, ArrowRight } from "lucide-react";
import { inr } from "../../lib/site";

const Row = ({ label, testid, value, min, max, step, onChange, format }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <label className="text-sm font-semibold text-brand-dark">{label}</label>
      <input data-testid={`${testid}-input`} type="number" className="input-site !w-32 !py-2 text-right font-bold"
        value={value} min={min} max={max} onChange={(e) => onChange(Number(e.target.value))} aria-label={label} />
    </div>
    <input data-testid={`${testid}-slider`} type="range" min={min} max={max} step={step} value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full accent-brand-red h-2 cursor-pointer" aria-label={`${label} slider`} />
    <div className="flex justify-between text-xs text-brand-muted mt-1">
      <span>{format(min)}</span><span>{format(max)}</span>
    </div>
  </div>
);

export default function EMICalculator({ defaults = {} }) {
  const [amount, setAmount] = useState(defaults.amount || 500000);
  const [rate, setRate] = useState(defaults.rate || 11);
  const [tenure, setTenure] = useState(defaults.tenure || 36);

  const { emi, totalInterest, totalPayable } = useMemo(() => {
    const r = rate / 1200;
    const n = tenure;
    const e = r > 0 ? (amount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1) : amount / n;
    const total = e * n;
    return { emi: Math.round(e), totalInterest: Math.round(total - amount), totalPayable: Math.round(total) };
  }, [amount, rate, tenure]);

  const data = [
    { name: "Principal", value: amount, color: "#111111" },
    { name: "Interest", value: totalInterest, color: "#C9151E" },
  ];

  return (
    <div data-testid="emi-calculator" className="card-site p-6 sm:p-10">
      <div className="grid lg:grid-cols-2 gap-10 items-center">
        <div className="space-y-7">
          <Row label="Loan Amount (₹)" testid="emi-amount" value={amount} min={25000} max={10000000} step={25000} onChange={setAmount} format={(v) => inr(v)} />
          <Row label="Interest Rate (% p.a.)" testid="emi-rate" value={rate} min={7} max={28} step={0.1} onChange={setRate} format={(v) => `${v}%`} />
          <Row label="Tenure (months)" testid="emi-tenure" value={tenure} min={6} max={360} step={6} onChange={setTenure} format={(v) => `${v} mo`} />
          <button data-testid="emi-reset-btn" onClick={() => { setAmount(500000); setRate(11); setTenure(36); }}
            className="inline-flex items-center gap-2 text-sm font-semibold text-brand-muted hover:text-brand-dark transition-colors duration-200">
            <RotateCcw size={14} /> Reset
          </button>
        </div>
        <div>
          <div className="h-56">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={data} dataKey="value" innerRadius={62} outerRadius={92} paddingAngle={3} strokeWidth={0}>
                  {data.map((d) => <Cell key={d.name} fill={d.color} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [inr(v), n]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-4 text-center">
            <div className="rounded-xl bg-brand-cream p-4">
              <p className="text-xs text-brand-muted font-semibold">Monthly EMI</p>
              <p data-testid="emi-result" className="font-heading text-lg sm:text-xl font-extrabold text-brand-red mt-1">{inr(emi)}</p>
            </div>
            <div className="rounded-xl bg-brand-cream p-4">
              <p className="text-xs text-brand-muted font-semibold">Total Interest</p>
              <p data-testid="emi-interest-result" className="font-heading text-lg sm:text-xl font-extrabold text-brand-dark mt-1">{inr(totalInterest)}</p>
            </div>
            <div className="rounded-xl bg-brand-cream p-4">
              <p className="text-xs text-brand-muted font-semibold">Total Payable</p>
              <p data-testid="emi-total-result" className="font-heading text-lg sm:text-xl font-extrabold text-brand-dark mt-1">{inr(totalPayable)}</p>
            </div>
          </div>
          <Link to="/apply" data-testid="emi-apply-btn" className="btn-primary w-full mt-5">Apply for This Loan <ArrowRight size={15} /></Link>
          <p className="mt-3 text-xs text-brand-muted text-center">Estimates use the standard reducing-balance method. Final EMI is determined by the lender.</p>
        </div>
      </div>
    </div>
  );
}
